import { PlaybackCtas } from '@/components/playback-ctas';
import { useAudio } from '@/hooks/use-audio';
import { useGuidePlayer } from '@/hooks/use-guide-player';
import { useGuideQueue } from '@/hooks/use-guide-queue';
import { useTogglePlayback } from '@/hooks/use-toggle-playback';
import { isActiveGuide } from '@/utils/guides';
import { MediaGuideMetadata } from '@/utils/media/types';
import { EnumButtonName, EventProperties } from '@calm/evexia-analytics';
import { ProgramNextButton } from '@rhg/components/program/next-button';
import { NextButtonOnClickHandler } from '@rhg/components/program/next-button/types';
import { useShuffleArm } from '@rhg/components/program/shuffle-arm';
import { useAudioControls } from '@rhg/providers/guide-player/active-player/use-audio-controls';
import { shuffleCapabilityForControls } from '@rhg/providers/guide-queue/capabilities';

interface HeaderPlaybackControlsProps {
    guides: MediaGuideMetadata[];
    packClass?: string;
    program: Parameters<typeof useAudioControls>[0]['program'];
    getStartGuide?: () => MediaGuideMetadata | undefined;
    onNextButtonClick: NextButtonOnClickHandler;
    playTrackButton?: EnumButtonName;
    playTrackProps?: EventProperties;
    shuffleTrackButton?: EnumButtonName;
    shuffleTrackProps?: EventProperties;
}

export function HeaderPlaybackControls({
    guides,
    packClass,
    program,
    getStartGuide,
    onNextButtonClick,
    playTrackButton,
    playTrackProps,
    shuffleTrackButton,
    shuffleTrackProps,
}: HeaderPlaybackControlsProps) {
    const { openGuidePlayer } = useGuidePlayer();
    const { controls, mediaEl } = useAudio();
    const togglePlayback = useTogglePlayback();
    const { toggleShuffle, isShuffled } = useGuideQueue();
    // EVE-13091: while nothing from this context is playing there is no queue to
    // toggle, so Shuffle arms an intent instead. Play (or picking a track from
    // the list) consumes it. The armed state is program-scoped and shared with
    // the track list via ShuffleArmProvider, so a picked track can start
    // shuffled too. Once the context is active the provider's `isShuffled` is
    // the source of truth.
    const { isShuffleArmed, setShuffleArmed } = useShuffleArm();

    const firstGuide = guides.find(({ isTrailer }) => !isTrailer);
    const audioControls = useAudioControls({
        program,
        playingGuide: firstGuide ?? guides[0],
    });
    const isShuffleEligible = shuffleCapabilityForControls(audioControls);

    // Content that isn't shuffle-eligible (clinical / standard program) keeps the
    // existing session/survey/trailer button unchanged.
    if (!isShuffleEligible) {
        return (
            <ProgramNextButton
                onClick={onNextButtonClick}
                {...(playTrackButton
                    ? { trackButton: playTrackButton }
                    : { skipTracking: true as const })}
            />
        );
    }

    const isActive = guides.some((g) => isActiveGuide(g, mediaEl));
    const isPlaying = isActive && !controls.isPaused;
    // On/off state shown on the Shuffle button: while the context is active it
    // tracks the real player state; otherwise it reflects the armed intent.
    const isShuffleOn = isActive ? isShuffled : isShuffleArmed;

    const onPlay = () => {
        // When this context is already loaded, resume or pause instead of
        // re-opening — tapping Play/Pause must never restart from the beginning.
        if (isActive) {
            togglePlayback();
            return;
        }
        // Shuffle armed on the detail page → open already shuffled, starting on a
        // random track (native parity). setQueue(willShuffle) builds the full
        // random playback order. Exclude trailers: active-player strips them from
        // the queue, and starting on a trailer yields `Regular` controls (no
        // shuffle), so the shuffle queue would never build. Consuming the armed
        // intent here — not on the Shuffle tap — is the EVE-13091 change.
        if (isShuffleArmed) {
            const nonTrailerGuides = guides.filter(
                ({ isTrailer }) => !isTrailer
            );
            const randomIndex = Math.floor(
                Math.random() * nonTrailerGuides.length
            );
            const startGuide = nonTrailerGuides[randomIndex] ?? guides[0];
            if (startGuide) {
                void openGuidePlayer({
                    guide: startGuide,
                    packClass,
                    willShuffle: true,
                    ...(playTrackProps ?? {}),
                });
            }
            setShuffleArmed(false);
            return;
        }
        // Nothing playing in this context → start from the first track (or the
        // caller's program-continuation override). Mirror useAudioControls
        // (line 91): fall back to the first non-trailer guide so Play never
        // starts on a trailer (which would load degraded `Regular` controls).
        const startGuide = getStartGuide?.() ?? firstGuide ?? guides[0];
        if (startGuide) {
            void openGuidePlayer({
                guide: startGuide,
                packClass,
                ...(playTrackProps ?? {}),
            });
        }
    };

    const onShuffle = () => {
        // Already playing this context → toggle shuffle in place.
        if (isActive) {
            toggleShuffle();
            return;
        }
        // EVE-13091: not playing → Shuffle only toggles the on/off state. It no
        // longer opens the player or autoplays; Play (or picking a track)
        // consumes the armed intent.
        setShuffleArmed(!isShuffleArmed);
    };

    return (
        <PlaybackCtas
            isPlaying={isPlaying}
            isShuffleOn={isShuffleOn}
            onPlay={onPlay}
            onShuffle={onShuffle}
            playTrackButton={playTrackButton}
            playTrackProps={playTrackProps}
            shuffleTrackButton={shuffleTrackButton}
            shuffleTrackProps={shuffleTrackProps}
        />
    );
}
